import { useState, useCallback, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "shared-utils";

import type {
  UsePlacesAutocompleteOptions,
  UsePlacesAutocompleteReturn,
  SelectedOption,
  ParsedAddress,
} from "./google-places";
import { PLACES_API_CONFIG } from "./google-places-constants";
import { usePlacesApi } from "./use-places-api";
import { createAddressParser, processAutocompleteOptions } from "./google-places-utils";

export const usePlacesAutocomplete = <T = any>(
  options: UsePlacesAutocompleteOptions<T>
): UsePlacesAutocompleteReturn => {
  const {
    debounceMs = PLACES_API_CONFIG.defaultDebounceMs,
    minQueryLength = PLACES_API_CONFIG.defaultMinQueryLength,
    states,
    countries,
    onAddressSelect,
    form,
    fieldName,
  } = options;

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOption, setSelectedOption] = useState<SelectedOption | null>(null);
  const [isInitialised, setIsInitialised] = useState(false);
  const [isManualEntry, setIsManualEntry] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, debounceMs);
  const { fetchPlaceDetails, fetchAutocomplete, cache } = usePlacesApi();
  const parseAddressComponents = useMemo(() => createAddressParser(), []);

  const {
    data: predictions = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["places-autocomplete", debouncedSearchTerm, countries, states],
    queryFn: () => fetchAutocomplete(debouncedSearchTerm, countries, minQueryLength),
    enabled: debouncedSearchTerm !== "" && debouncedSearchTerm?.length >= minQueryLength,
    staleTime: PLACES_API_CONFIG.staleTime,
  });

  const initialiseFromFormValue = useCallback(async () => {
    if (isInitialised || !form || !fieldName || selectedOption) {
      return;
    }

    const formValue = form.getValues(fieldName);
    if (formValue && typeof formValue === "string") {
      // Skip API call if value looks like display text (contains commas)
      // Not great, but avoids unnecessary calls
      if (formValue.includes(",")) {
        setIsInitialised(true);
        return;
      }

      const cachedPlaceDetails = cache.getCachedPlaceDetails(formValue);
      const cachedOriginalText = cache.getCachedOriginalText(formValue);

      if (cachedPlaceDetails && cachedOriginalText) {
        const address = parseAddressComponents(cachedPlaceDetails.addressComponents);
        address.formattedAddress = cachedPlaceDetails.formattedAddress;

        setSelectedOption({
          id: formValue,
          text: cachedOriginalText,
          address,
        });
        setSearchTerm(cachedOriginalText);
        setIsInitialised(true);
        return;
      }

      const placeDetails = await fetchPlaceDetails(formValue);
      if (placeDetails) {
        const address = parseAddressComponents(placeDetails.addressComponents);
        address.formattedAddress = placeDetails.formattedAddress;

        setSelectedOption({
          id: formValue,
          text: placeDetails.formattedAddress,
          address,
        });
        setSearchTerm(placeDetails.formattedAddress);
      }
    }
    setIsInitialised(true);
  }, [isInitialised, form, fieldName, fetchPlaceDetails, parseAddressComponents, cache, selectedOption]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    initialiseFromFormValue();
  }, [initialiseFromFormValue]);

  const onSearchTermChange = useCallback(
    (newSearchTerm: string) => {
      if (newSearchTerm === searchTerm) {
        return;
      }

      if (selectedOption) {
        if (!newSearchTerm || newSearchTerm === selectedOption.text) {
          return;
        }
        setSelectedOption(null);
        setIsManualEntry(true);
      } else if (newSearchTerm) {
        setIsManualEntry(true);
      }

      setSearchTerm(newSearchTerm);
    },
    [searchTerm, selectedOption]
  );

  const onChange = useCallback(
    async (option: any) => {
      if (!option) {
        setSelectedOption(null);
        setIsManualEntry(false);
        if (form && fieldName) {
          form.setValue(fieldName, "");
        }
        return;
      }

      setIsManualEntry(false);

      const initialAddress: ParsedAddress = { formattedAddress: option.text };
      const mainText = option.prediction?.structured_formatting?.main_text?.text;

      setSelectedOption({
        id: option.id,
        text: option.text,
        address: initialAddress,
      });

      const placeDetails = await fetchPlaceDetails(option.prediction.place_id);
      if (placeDetails) {
        const detailedAddress = parseAddressComponents(placeDetails.addressComponents);
        detailedAddress.formattedAddress = placeDetails.formattedAddress;

        if (states?.length && detailedAddress.state) {
          const upperStates = states.map((s) => s.toUpperCase());
          if (!upperStates.includes(detailedAddress.state.toUpperCase())) {
            setSelectedOption(null);
            setSearchTerm("");
            if (form && fieldName) {
              form.setValue(fieldName, "");
            }
            return;
          }
        }

        cache.cacheOriginalText(option.id, option.text);

        setSelectedOption((prev) =>
          prev
            ? {
                ...prev,
                address: detailedAddress,
              }
            : null
        );

        if (form && fieldName) {
          form.setValue(fieldName, option.id);
        }

        await onAddressSelect?.(detailedAddress, placeDetails, mainText);
      }
    },
    [fetchPlaceDetails, form, fieldName, parseAddressComponents, cache, onAddressSelect, states]
  );

  const clear = useCallback(() => {
    setSearchTerm("");
    setSelectedOption(null);
    if (form && fieldName) {
      form.setValue(fieldName, "");
    }
  }, [form, fieldName]);

  const setManualAddress = useCallback((address: ParsedAddress, displayText: string) => {
    setSelectedOption({
      id: displayText,
      text: displayText,
      address: address,
    });

    setSearchTerm(displayText);
    setIsManualEntry(true);

    // NOTE: Do NOT set form.setValue here - the caller already sets the form field.
    // Setting it here would be redundant and could create inconsistencies.
  }, []);

  const autocompleteOptions = useMemo(() => processAutocompleteOptions(predictions, states), [predictions, states]);

  return {
    options: autocompleteOptions,
    fetching: isLoading,
    error: error?.message || null,
    onSearchTermChange,
    onChange,
    searchTerm,
    selectedAddress: selectedOption?.address || null,
    selectedDisplayText: selectedOption?.text || null,
    clear,
    isManualEntry,
    setManualAddress,
  };
};

export { createAddressFieldSetters } from "./google-places-utils";
